import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Modal, Toast, useIsMobile, useT } from '@repo/common';
import RefundModal from '../../Kpathport/Modal/RefundModal';
import MyWorkshops from './MyWorkshops/MyWorkshops';
import MyMvps from './MyMvps/MyMvps';
import MyQna from './MyQna/MyQna';
import {
  cancelBooking,
  getConfirmedWorkshopDetails,
} from '../../../api/booking/booking.api';
import { getMyLikedWorkshops } from '../../../api/workshop/workshopLike.api';
import { getMyMVPs } from '../../../api/workshop/mvp.api';
import { getMyQuestionList } from '../../../api/qna/qna.api';

const WORKSHOP_FILTERS = new Set(['Upcoming', 'Waitlist', 'Saved', 'Past']);
const SECTION_TABS = ['My Workshops', 'My MVPs', 'My Q&A'];
const SECTION_TABS_KR = {
  'My Workshops': '나의 워크숍',
  'My MVPs': '나의 MVP',
  'My Q&A': '나의 Q&A',
};

const QNA_PREVIEW_COUNT = 2;
const QNA_STEP = 3;
const getWorkshopFilterFromSearch = (search) => {
  const filter = new URLSearchParams(search).get('workshopFilter');
  return WORKSHOP_FILTERS.has(filter) ? filter : null;
};

const Activities = () => {
  const t = useT();
  const location = useLocation();
  const isMobile = useIsMobile();
  const initialWorkshopFilter = getWorkshopFilterFromSearch(location.search);
  const [workshopsList, setWorkshopsList] = useState([]);
  const [hasAnyWorkshop, setHasAnyWorkshop] = useState(false);
  const [likedWorkshopsList, setLikedWorkshopsList] = useState([]);
  const [mvpsList, setMvpsList] = useState([]);
  const [qnaList, setQnaList] = useState([]);
  const [qnaPage, setQnaPage] = useState(0);
  const [hasMoreQna, setHasMoreQna] = useState(true);
  const [qnaStorage, setQnaStorage] = useState([]);
  const [serverHasMore, setServerHasMore] = useState(true);

  const qnaSectionRef = useRef(null);
  const workshopsSectionRef = useRef(null);
  const mvpsSectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState('My Workshops');

  const [currentFilter, setCurrentFilter] = useState(
    initialWorkshopFilter || 'Upcoming',
  );
  const [currentWorkshopPage, setCurrentWorkshopPage] = useState(1);
  const [totalWorkshopPages, setTotalWorkshopPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [modalType, setModalType] = useState('NORMAL');
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showLikeDeleteSuccessToast, setShowLikeDeleteSuccessToast] =
    useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      if (currentFilter === 'Saved') {
        const likedData = await getMyLikedWorkshops(currentWorkshopPage - 1, 3);
        setLikedWorkshopsList(likedData.content || []);
        setTotalWorkshopPages(likedData.totalPages || 1);
      } else {
        const data = await getConfirmedWorkshopDetails(
          currentWorkshopPage - 1,
          3,
          currentFilter,
        );
        setWorkshopsList(data.content || []);
        setTotalWorkshopPages(data.totalPages || 1);
      }

      const mvpData = await getMyMVPs();
      setMvpsList(mvpData || []);

      const qnaData = (await getMyQuestionList(QNA_STEP, 0)) || [];
      setQnaStorage(qnaData);
      setQnaList(qnaData.slice(0, QNA_PREVIEW_COUNT));
      setQnaPage(1);
      setServerHasMore(qnaData.length === QNA_STEP);
      setHasMoreQna(qnaData.length > QNA_PREVIEW_COUNT);
    } catch (error) {
      console.error('확정된 예약 내역을 불러오지 못했습니다.', error);
    }
  }, [currentWorkshopPage, currentFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    const nextFilter = getWorkshopFilterFromSearch(location.search);
    if (!nextFilter) return;

    setCurrentFilter((prevFilter) =>
      prevFilter === nextFilter ? prevFilter : nextFilter,
    );
    setCurrentWorkshopPage(1);
  }, [location.search]);

  useEffect(() => {
    const checkInitialData = async () => {
      const allData = await getConfirmedWorkshopDetails(0, 1, 'Upcoming');
      setHasAnyWorkshop(allData.content && allData.content.length > 0);
    };
    checkInitialData();
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const sections = [
      { tab: 'My Workshops', ref: workshopsSectionRef },
      { tab: 'My MVPs', ref: mvpsSectionRef },
      { tab: 'My Q&A', ref: qnaSectionRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const tab = visible[0].target.dataset.tab;
          if (tab) setActiveTab(tab);
        }
      },
      {
        rootMargin: '-40px 0px -60% 0px',
        threshold: 0,
      },
    );

    sections.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    setCurrentWorkshopPage(1);
  };

  const handleWorkshopPageChange = (page) => {
    setCurrentWorkshopPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelClick = (bookingId, isWaitlist) => {
    setSelectedBookingId(bookingId);
    setModalType(isWaitlist ? 'WAITLIST' : 'NORMAL');
    setIsModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      await cancelBooking(selectedBookingId);

      setIsModalOpen(false);
      fetchHistory();

      setShowDeleteSuccessToast(false);
      setTimeout(() => setShowDeleteSuccessToast(true), 100);
    } catch (error) {
      console.error('취소에 실패했습니다.', error);
    } finally {
      setIsCanceling(false);
    }
  };

  const toastLabel = () => {
    if (modalType === 'WAITLIST') {
      return t('Waitlist canceled.', '대기 신청이 취소되었습니다.');
    }
    return t('Booking canceled.', '예약이 취소되었습니다.');
  };

  const handleLikeDeleteSuccess = () => {
    setShowLikeDeleteSuccessToast(false);
    setTimeout(() => setShowLikeDeleteSuccessToast(true), 100);
    fetchHistory();
  };

  const handleLoadMoreQna = async () => {
    if (!hasMoreQna) {
      setQnaList(qnaStorage.slice(0, QNA_PREVIEW_COUNT));
      setHasMoreQna(qnaStorage.length > QNA_PREVIEW_COUNT || serverHasMore);
      qnaSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }

    const target = qnaList.length + QNA_STEP;
    let storage = qnaStorage;
    let page = qnaPage;
    let canFetchMore = serverHasMore;

    if (storage.length < target && canFetchMore) {
      try {
        const fetched = (await getMyQuestionList(QNA_STEP, page)) || [];
        storage = storage.concat(fetched);
        page += 1;
        canFetchMore = fetched.length === QNA_STEP;
      } catch (error) {
        console.error('QnA를 더 불러오는데 실패했습니다.', error);
        return;
      }
    }

    const nextList = storage.slice(0, target);

    setQnaStorage(storage);
    setQnaPage(page);
    setServerHasMore(canFetchMore);
    setQnaList(nextList);
    setHasMoreQna(nextList.length < storage.length || canFetchMore);
  };

  const sectionRefs = {
    'My Workshops': workshopsSectionRef,
    'My MVPs': mvpsSectionRef,
    'My Q&A': qnaSectionRef,
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    sectionRefs[tab]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className='flex flex-col w-full items-start'>
      {isMobile && (
        <div className='sticky top-0 z-20 w-[calc(100%+32px)] -mx-4 flex gap-4 p-4 pb-0 bg-grayscale-gray01 border-b border-grayscale-gray02'>
          {SECTION_TABS.map((tab) => (
            <button
              key={tab}
              type='button'
              onClick={() => handleTabClick(tab)}
              className={`whitespace-nowrap pb-2 typo-b13 transition-colors ${
                activeTab === tab
                  ? 'text-text-primary border-collapse border-b-2 border-b-green-deep02'
                  : 'text-text-secondary'
              }`}
            >
              {t(tab, SECTION_TABS_KR[tab])}
            </button>
          ))}
        </div>
      )}

      <div className='flex flex-col w-full items-start gap-[60px] pt-6'>
        <div
          ref={workshopsSectionRef}
          data-tab='My Workshops'
          className='w-full scroll-mt-[20px]'
        >
          <MyWorkshops
            workshopsList={workshopsList}
            likedWorkshopsList={likedWorkshopsList}
            onLikeDeleteSuccess={handleLikeDeleteSuccess}
            onExpire={fetchHistory}
            onCancelClick={handleCancelClick}
            currentFilter={currentFilter}
            onFilterChange={handleFilterChange}
            current={currentWorkshopPage}
            totalPages={totalWorkshopPages}
            onPageChange={handleWorkshopPageChange}
            hasAnyWorkshop={hasAnyWorkshop}
          />
        </div>

        <div
          ref={mvpsSectionRef}
          data-tab='My MVPs'
          className='w-full scroll-mt-[40px]'
        >
          <MyMvps mvpsList={mvpsList} />
        </div>

        <div
          ref={qnaSectionRef}
          data-tab='My Q&A'
          className='w-full scroll-mt-[40px]'
        >
          <MyQna
            qnaList={qnaList}
            hasMore={hasMoreQna}
            onLoadMore={handleLoadMoreQna}
            totalCount={qnaStorage.length}
          />
        </div>

        {isModalOpen && (
          <Modal
            isOpen
            onClickLeftBtn={() => setIsModalOpen(false)}
            onClickRightBtn={handleConfirmCancel}
            title={
              modalType === 'WAITLIST'
                ? t('Cancel waitlist request?', '대기 신청을 취소하시겠습니까?')
                : t('Cancel this booking?', '이 예약을 취소하시겠습니까?')
            }
            subText={
              modalType === 'WAITLIST'
                ? t(
                    `This action can't be undone.`,
                    '이 작업은 되돌릴 수 없습니다.',
                  )
                : t(
                    `This action can't be undone. \nRefunds follow our policy.`,
                    '이 작업은 되돌릴 수 없습니다. \n환불은 환불 정책에 따라 진행됩니다.',
                  )
            }
            links={
              modalType === 'NORMAL' && [
                {
                  text: t('View refund policy', '환불 정책 보기'),
                  onClick: () => setIsRefundModalOpen(true),
                },
              ]
            }
            linkMarginClass='mt-0'
            linkclass='text-text-secondary'
            buttonSize='xl'
            leftButtonLabel={t('Go Back', '돌아가기')}
            leftHideIcon={true}
            rightButtonLabel={
              modalType === 'WAITLIST'
                ? t('Cancel Waitlist', '대기 취소')
                : t('Cancel Booking', '예약 취소')
            }
            rightHideIcon={true}
            rightButtonLoading={isCanceling}
            type='warning'
          />
        )}

        {isRefundModalOpen && (
          <RefundModal
            isOpen={isRefundModalOpen}
            onClose={() => setIsRefundModalOpen(false)}
          />
        )}

        {showDeleteSuccessToast && (
          <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
            <div className='w-full max-w-[240px] tabletH:w-[240px]'>
              <Toast
                type='success'
                state='white'
                title={toastLabel()}
                direction='up'
                customWidth='100%'
              />
            </div>
          </div>
        )}

        {showLikeDeleteSuccessToast && (
          <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
            <div className='w-full max-w-[240px] tabletH:w-[240px]'>
              <Toast
                type='success'
                state='white'
                title={t('Workshop unliked.', '찜이 해제되었습니다.')}
                direction='up'
                customWidth='100%'
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;
